
import { useNavigate } from 'react-router-dom';
import { TelemetryClientService } from '../services/TelemetryClientService';
import { SurveyView } from './SurveyView';
import React from 'react';
import { ICourse } from '@/shared/ICourse';
import { WorkflowService } from '@/shared/WorkflowService';
import { IWorkflowAction } from '@/shared/IWorkflowAction';
import { Box, Center, Container, Spinner, useBreakpointValue, useToast } from '@chakra-ui/react';
import { AppContext } from '@/app/comments/AppContext';
import { CourseModuleStorageService } from '../services/CourseModuleStorageService';
import { stringifyError } from '@/shared/parse';
import { ICourseModule } from '@/shared/ICourseModule';
import { AuthService } from '@/app/services/AuthService';
import { ReadOnlyNotice } from '../comments/ReadOnlyNotice';
import { WorkflowTextService } from '@/shared/WorkflowTextService';
import { CourseReviewSidebar } from '../review/CourseReviewSidebar';
import { CourseStorageService } from '../services/CourseStorageService';
import { FILENAMES, STEPS } from '@/shared/constants';
import { CourseAmendmentService } from '../services/CourseAmendmentService';
import { AppBreadcrumb } from '../navbar/AppBreadcrumb';

export const SimpleSurveyPage = (props: {
  course: ICourse,
  setCourse: (course: ICourse | undefined) => void,
  moduleId: string,
  action: string,
  pageId?: string
}) => {

  const { course, setCourse } = props;
  const { setStatus } = React.useContext(AppContext);
  const [isLoading, setIsLoading] = React.useState(false);

  const navigate = useNavigate();

  const onComplete = async (data: any) => {

    // apply results to the original submission and remove the results
    if (props.moduleId === STEPS.COMPLETED) {
      await CourseAmendmentService.putCourseAmendment(course, data);
    }

    navigate(`/course/${course.id}`);
  }

  const toast = useToast();

  const [actions, setActions] = React.useState<IWorkflowAction[]>([]);
  const [action, setAction] = React.useState<string>();
  const [courseModule, setCourseModule] = React.useState<ICourseModule | undefined>();

  const onPageChanged = (pageId: string) => {
    navigate(`/course/${course.id}/${props.moduleId}/${action}/${pageId}`, { replace: true });
  }

  const onShowFeedback = () => {
    setIsFeedbackSidebarOpen(true);
  }

  const breadcrumb = React.useMemo(() => [
    { href: '/courses', label: 'Courses' },
    { href: `/course/${course.id}`, label: course.folderName },
    { href: `/course/${course.id}/${props.moduleId}/${action}`, label: props.moduleId },
  ], [course, props.moduleId, action]);

  const load = async (force: boolean) => {
    try {

      let loadedModules = await CourseModuleStorageService.getCourseModules(course.id, force);
      let loadedModule: ICourseModule | undefined;
      let loadedAction = props.action;

      loadedModule = await CourseModuleStorageService.getCourseModule(course.id, props.moduleId, props.action, force);
      if (loadedAction === 'new') {
        navigate(`/course/${course.id}/${props.moduleId}/edit`, { replace: true });
        loadedAction = 'edit';
      }

      setCourseModule(loadedModule);

      const roles = AuthService.getCourseRoles(course);
      const loadedActions = WorkflowService.getActions(roles, course, props.moduleId, loadedModules) || [];
      setActions(loadedActions);
      if (!loadedActions.some(x => x.id === loadedAction
        || x.id == 'edit' && loadedAction === 'new'
        || x.id === 'new' && loadedAction === 'edit'
        || x.id === 'new-small-changes' && loadedAction === 'edit'
        || x.id === 'new-small-changes' && loadedAction === 'new'
      )) {
        loadedAction = 'view'
      }
      setAction(loadedAction);

    } catch (err) {
      TelemetryClientService.error(`Unable to load course ${course.id} module ${props.moduleId}`, err);
      toast({
        title: "Unable to load course module",
        description: stringifyError(err),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }

  const onRefresh = async () => {
    try {
      setIsLoading(true);
      setStatus('Loading...');
      const loadedCourse = await CourseStorageService.getCourse(course.id, true)
      setCourse(loadedCourse);
      await load(true);
    } finally {
      setStatus(undefined);
      setIsLoading(false);
    }
  }

  React.useEffect(() => {
    if (props.moduleId && props.action) {
      load(false);
    }
  }, [props.moduleId, props.action]);

  const isExternalExpert = AuthService.isExternalExpert(course);

  TelemetryClientService.page(`SurveyPage-${props.moduleId}`);

  const [isFeedbackSidebarOpen, setIsFeedbackSidebarOpen] = React.useState(false);
  const allowPin = !!useBreakpointValue({ base: false, md: true });
  const margin = isFeedbackSidebarOpen && allowPin ? 30 : 0;
  const ml = margin ? `calc(${margin}em + 2em)` : `0`;
  const width = margin ? `calc(100% - (${margin}em  + 2em))` : 'full';

  const pdfFileName = WorkflowTextService.getPdfFileName(course.folderName, props.moduleId, false);
  const pdfFileTitle = WorkflowTextService.getPdfFileTitle(props.moduleId, false);

  return <>
    <AppBreadcrumb breadcrumb={breadcrumb} />
    {courseModule ?
      <Box top={120} bottom='1' pos='absolute' ml={ml} width={width} overflow='hidden' display='flex'>
        {action && <SurveyView
          course={course}
          module={courseModule}
          action={action}
          pageId={props.pageId}
          onPageChanged={onPageChanged}
          showFeedback={!!courseModule.feedback}
          onShowFeedback={onShowFeedback}
          onComplete={onComplete}
          onRefresh={onRefresh}
          enableComments={!isExternalExpert}
          showMarkup={action !== 'view'}
          showSubmit={action !== 'view'}
          showMenu={true}
          isLoading={isLoading}
          moduleTemplate={courseModule.results_template}
          moduleDataProperty='results'
          moduleFileName={FILENAMES.RESULTS}
          pdfFileName={pdfFileName}
          pdfFileTitle={pdfFileTitle}
        />}
        {action === 'view' && <ReadOnlyNotice course={course} module={courseModule} actions={actions} />}
        <CourseReviewSidebar
          label="Feedback"
          allowPin={allowPin}
          isOpen={isFeedbackSidebarOpen} onClose={() => setIsFeedbackSidebarOpen(false)} >
          <SurveyView
            course={course}
            module={courseModule}
            action='view'
            moduleTemplate={courseModule.feedback_template}
            moduleDataProperty='feedback'
            moduleFileName={''}
            enableComments={false}
            showMarkup={false}
          />
        </CourseReviewSidebar>
      </Box>
      : <Container mt={200}>
        <Center color='fg.muted' fontSize='large'><Spinner size='lg' /><Box ml={4} >Loading questionnaire...</Box></Center>
      </Container>}
  </>
}